import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { Anchors } from '../buildings/Anchors';
import { flatten, type PropModel } from '../props/PropModels';
import type { BannerSpec } from '../../network/Banners';
import { bannerCloth } from './BannerCloth';

/**
 * Sztandar w całości: drzewce z pliku i płat gracza przybity do niego.
 *
 * **Drzewce jest modelem-zastępstwem** (`banner-1.glb`), a gdzie płat jest do
 * niego przybity, mówią punkty `attach` ustawiane na stronie Modele — górny
 * i dolny. Płat jest rysowany kodem między nimi, więc podmiana drzewca na
 * prawdziwy model nie wymaga zmiany ani linii tutaj.
 */

export const STAFF_FILE = 'banner-1';

/** Gdy model drzewca nie ma punktów: płat od 95% do 60% jego wysokości. */
const DEFAULT_ATTACH: [THREE.Vector3, THREE.Vector3] = [
  new THREE.Vector3(0, 0.95, 0),
  new THREE.Vector3(0, 0.6, 0),
];

export interface Staff {
  model: PropModel;
  top: THREE.Vector3;
  bottom: THREE.Vector3;
}

export async function loadStaff(anchors: Anchors): Promise<Staff> {
  const gltf = await new GLTFLoader().loadAsync(`/models/${STAFF_FILE}.glb`);
  const model = flatten(STAFF_FILE, gltf.scene);
  const points = anchors[STAFF_FILE]?.attach;
  const top = points?.[0] ? new THREE.Vector3(...points[0]) : DEFAULT_ATTACH[0];
  const bottom = points?.[1] ? new THREE.Vector3(...points[1]) : DEFAULT_ATTACH[1];
  return { model, top, bottom };
}

/**
 * Drzewce wysokości `height` z płatem, stojące podstawą w początku układu.
 * Płat powiewa wzdłuż +X lokalnego układu.
 */
export function raiseBanner(
  staff: Staff,
  texture: THREE.Texture,
  spec: BannerSpec,
  height: number
): THREE.Object3D {
  const root = new THREE.Group();
  root.name = 'banner';

  const pole = new THREE.Group();
  for (const part of staff.model.parts) {
    const mesh = new THREE.Mesh(part.geometry, part.material);
    mesh.castShadow = true;
    pole.add(mesh);
  }
  // `flatten` daje model o wysokości jeden, więc punkty też są w tej skali.
  pole.scale.setScalar(height);
  root.add(pole);

  const top = staff.top.clone().multiplyScalar(height);
  const bottom = staff.bottom.clone().multiplyScalar(height);
  const cloth = bannerCloth(texture, Math.max(0.01, top.distanceTo(bottom)), spec);
  cloth.position.copy(top);
  root.add(cloth);

  return root;
}
